import styled from 'styled-components';
import {ButtonDropdown, DropdownHandler, EMPHASIS, Label} from '@forecast-it/design-system';
import {GROUP_BY_OPTIONS, type DashboardGroupByToken} from '../utils/groupTickets';

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;

interface Props {
	value: DashboardGroupByToken;
	onChange: (next: DashboardGroupByToken) => void;
}

const [NO_GROUPING, ...GROUPING_OPTIONS] = GROUP_BY_OPTIONS;

/**
 * Single-select dropdown for the dashboard's active grouping, defaulting to
 * "No grouping". Grouping is purely client-side and re-fetches nothing.
 *
 * "No grouping" sits above a separator as its own item rather than inside the
 * mapped list — it is the way out of grouping, not one of the things you can group
 * by, and the same shape the queue's quick filters use for their "All" reset.
 */
export function DashboardGroupByControl({value, onChange}: Props) {
	const currentLabel = GROUP_BY_OPTIONS.find(o => o.id === value)?.label ?? NO_GROUPING!.label;

	return (
		<Row data-testid="dashboard-group-by-control">
			<Label>Group by</Label>
			<DropdownHandler>
				<DropdownHandler.Trigger>
					<ButtonDropdown emphasis={EMPHASIS.LOW} data-cy="dashboard-group-by">
						{currentLabel}
					</ButtonDropdown>
				</DropdownHandler.Trigger>
				<DropdownHandler.Content offset={4}>
					<DropdownHandler.Item
						data-cy={`dashboard-group-by-${NO_GROUPING!.id}`}
						onClick={() => onChange(NO_GROUPING!.id)}
					>
						{NO_GROUPING!.label}
					</DropdownHandler.Item>
					<DropdownHandler.Separator />
					{GROUPING_OPTIONS.map(option => (
						<DropdownHandler.Item
							key={option.id}
							data-cy={`dashboard-group-by-${option.id}`}
							onClick={() => onChange(option.id)}
						>
							{option.label}
						</DropdownHandler.Item>
					))}
				</DropdownHandler.Content>
			</DropdownHandler>
		</Row>
	);
}
